import { useState, useEffect, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import axios from 'axios'
import clsx from 'clsx'
import { buildApiUrl, ELECTION_ENDPOINTS, GEO_ENDPOINTS } from '../../../config/api'
import { SlidePanel } from '../../../components/ui'
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell, AreaChart, Area, CartesianGrid
} from 'recharts'
import {
  MapPin, Users, Vote, TrendingUp, Award, Building2,
  ChevronRight, Filter, ChevronDown, Search, BarChart3,
  Landmark, UserCheck, UserX, FileX2, Loader2
} from 'lucide-react'

const CHART_COLORS = ['#465fff', '#12b76a', '#f79009', '#f04438', '#0ba5ec', '#8b5cf6', '#ec4899', '#14b8a6', '#f59e0b', '#6366f1', '#84cc16', '#06b6d4', '#e11d48', '#7c3aed', '#059669', '#dc2626']

export default function ElectionDashboardPage() {
  const navigate = useNavigate()
  const [loading, setLoading] = useState(true)
  const [elections, setElections] = useState<any[]>([])
  const [states, setStates] = useState<any[]>([])
  const [cities, setCities] = useState<any[]>([])
  const [selectedElection, setSelectedElection] = useState<number | null>(null)
  const [selectedState, setSelectedState] = useState('')
  const [selectedCity, setSelectedCity] = useState('')
  const [cityName, setCityName] = useState('')
  const [uf, setUf] = useState('')
  const [dashboard, setDashboard] = useState<any>(null)
  const [zones, setZones] = useState<any[]>([])
  const [neighborhoods, setNeighborhoods] = useState<any[]>([])
  const [selectedPosition, setSelectedPosition] = useState('')
  const [activeTab, setActiveTab] = useState('geral')
  const [showFilters, setShowFilters] = useState(true)
  const [candidatePanel, setCandidatePanel] = useState<any>(null)
  const [candidatePanelOpen, setCandidatePanelOpen] = useState(false)
  const [candidateLoading, setCandidateLoading] = useState(false)
  const [candidateTab, setCandidateTab] = useState<'bairros' | 'zonas' | 'secoes'>('bairros')
  const [selectedCandidates, setSelectedCandidates] = useState<number[]>([])
  const [compareData, setCompareData] = useState<any>(null)
  const [compareOpen, setCompareOpen] = useState(false)
  const [compareLoading, setCompareLoading] = useState(false)
  const [compareTab, setCompareTab] = useState<'zonas' | 'bairros'>('zonas')
  const [zoneResults, setZoneResults] = useState<any>(null)
  const [bairroResults, setBairroResults] = useState<any>(null)
  const [zoneLoading, setZoneLoading] = useState(false)
  const [bairroLoading, setBairroLoading] = useState(false)

  // Carrega dados iniciais + config da org
  useEffect(() => {
    const load = async () => {
      try {
        const [elRes, stRes, configRes] = await Promise.all([
          axios.get(buildApiUrl(ELECTION_ENDPOINTS.ELECTIONS)),
          axios.get(buildApiUrl(GEO_ENDPOINTS.STATES)),
          axios.get(buildApiUrl('/v1/political/config')).catch(() => ({ data: { data: {} } })),
        ])

        const el = elRes.data.data || []
        const st = stRes.data.data || []
        setElections(el)
        setStates(st)

        if (el.length > 0) setSelectedElection(el[0].id)

        // Auto-seleciona estado/cidade da org
        const config = configRes.data?.data?.config
        if (config?.geo_state_id) {
          setSelectedState(String(config.geo_state_id))
          const state = st.find((s: any) => s.id === config.geo_state_id)
          if (state) setUf(state.code)

          // Carrega cidades e auto-seleciona
          if (config.geo_state_id) {
            const citiesRes = await axios.get(buildApiUrl(GEO_ENDPOINTS.CITIES, { stateId: String(config.geo_state_id) }))
            setCities(citiesRes.data.data || [])

            if (config.geo_city_id) {
              setSelectedCity(String(config.geo_city_id))
              const city = (citiesRes.data.data || []).find((c: any) => c.id === config.geo_city_id)
              if (city) setCityName(city.name)
            }
          }
        }
      } catch { /* */ }
      setLoading(false)
    }
    load()
  }, [])

  // Carrega cidades quando muda estado
  useEffect(() => {
    if (!selectedState) return
    const state = states.find((s: any) => s.id === Number(selectedState))
    if (state) setUf(state.code)
    axios.get(buildApiUrl(GEO_ENDPOINTS.CITIES, { stateId: selectedState }))
      .then(r => setCities(r.data.data || []))
      .catch(() => {})
  }, [selectedState, states])

  // Carrega dashboard
  const loadDashboard = useCallback(async () => {
    const elData = elections.find((e: any) => e.id === selectedElection)
    const isEst = elData?.type === 'ESTADUAL_FEDERAL'

    // Municipal: precisa de cidade. Estadual: precisa de estado.
    if (!selectedElection) return
    if (isEst && !uf) return
    if (!isEst && !selectedCity) return

    setLoading(true)
    try {
      const params = isEst
        ? { election_id: selectedElection, uf }
        : { city_id: selectedCity, election_id: selectedElection, uf }

      const [dashRes, zoneRes] = await Promise.all([
        axios.get(buildApiUrl(ELECTION_ENDPOINTS.RESULTS_OVERVIEW), { params }),
        axios.get(buildApiUrl(ELECTION_ENDPOINTS.ZONES), { params: isEst ? { uf } : { city_id: selectedCity, uf } }),
      ])
      setDashboard(dashRes.data.data)
      const cargos = dashRes.data.data.comparecimento_por_cargo || []
      if (cargos.length > 0 && !selectedPosition) setSelectedPosition(cargos[0].position_code)
      setActiveTab('geral')
      setZones((zoneRes.data.data || []).map((z: any) => ({ ...z, bairros: typeof z.bairros === 'string' ? JSON.parse(z.bairros) : z.bairros || [] })))
    } catch { /* */ }
    setLoading(false)
  }, [selectedCity, selectedElection, uf, elections])

  useEffect(() => {
    const elData = elections.find((e: any) => e.id === selectedElection)
    const isEst = elData?.type === 'ESTADUAL_FEDERAL'
    if (isEst && uf) loadDashboard()
    else if (!isEst && selectedCity) loadDashboard()
  }, [loadDashboard, selectedCity, selectedElection, uf, elections])

  const handleCityChange = (cityId: string) => {
    setSelectedCity(cityId)
    const city = cities.find((c: any) => c.id === Number(cityId))
    setCityName(city?.name || '')
  }

  function toggleCandidate(id: number) {
    setSelectedCandidates(prev => prev.includes(id) ? prev.filter(c => c !== id) : [...prev, id])
  }

  async function compareCandidates() {
    if (selectedCandidates.length < 2) return
    setCompareLoading(true)
    setCompareOpen(true)
    setCompareTab('zonas')
    try {
      const res = await axios.get(buildApiUrl(ELECTION_ENDPOINTS.RESULTS_CANDIDATES_COMPARE), { params: { ids: selectedCandidates } })
      setCompareData(res.data.data)
    } catch { /* */ } finally { setCompareLoading(false) }
  }

  async function openCandidateProfile(candidateId: number) {
    setCandidatePanelOpen(true)
    setCandidateLoading(true)
    setCandidateTab('bairros')
    try {
      const res = await axios.get(buildApiUrl(ELECTION_ENDPOINTS.RESULTS_CANDIDATE, { candidateId: String(candidateId) }))
      setCandidatePanel(res.data.data)
    } catch { /* */ } finally { setCandidateLoading(false) }
  }

  const fmt = (n: any) => n ? Number(n).toLocaleString('pt-BR') : '0'

  const d = dashboard
  const comp = d?.comparecimento
  const totalEleitores = zones.reduce((s: number, z: any) => s + Number(z.total_eleitores || 0), 0)
  const totalSecoes = zones.reduce((s: number, z: any) => s + Number(z.total_secoes || 0), 0)
  const totalLocais = zones.reduce((s: number, z: any) => s + Number(z.total_locais_votacao || 0), 0)

  const selectedElectionData = elections.find((e: any) => e.id === selectedElection)
  const isEstadual = selectedElectionData?.type === 'ESTADUAL_FEDERAL'
  const stateName = states.find((s: any) => s.id === Number(selectedState))?.name || ''

  return (
    <div className="space-y-5">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="text-xl font-bold text-gray-900">Histórico de Eleições</h1>
          <p className="text-sm text-gray-500 mt-0.5">Inteligência eleitoral por zona, seção e bairro</p>
        </div>
        <button
          onClick={() => setShowFilters(!showFilters)}
          className="flex items-center justify-center gap-2 px-4 py-2.5 bg-white border border-gray-200 rounded-xl text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors w-full sm:w-auto"
        >
          <Filter className="w-4 h-4" />
          Filtros
          <ChevronDown className={`w-3.5 h-3.5 transition-transform ${showFilters ? 'rotate-180' : ''}`} />
        </button>
      </div>

      {/* Filtros colapsáveis */}
      {showFilters && (
        <div className="bg-white rounded-2xl border border-gray-200 p-5 animate-in slide-in-from-top-2">
          <div className={`grid grid-cols-1 gap-4 ${isEstadual ? 'sm:grid-cols-2' : 'sm:grid-cols-3'}`}>
            <div>
              <label className="block text-[11px] font-semibold text-gray-400 uppercase tracking-wider mb-1.5">Eleição</label>
              <select value={selectedElection || ''} onChange={e => { setSelectedElection(Number(e.target.value)); setSelectedCity(''); setCityName('') }}
                className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm bg-gray-50 focus:ring-2 focus:ring-brand-500 focus:border-brand-500 focus:bg-white transition-colors">
                {elections.map((el: any) => (
                  <option key={el.id} value={el.id}>{el.year} — {el.type === 'MUNICIPAL' ? 'Municipal' : 'Estadual/Federal'} ({el.round}º turno)</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-[11px] font-semibold text-gray-400 uppercase tracking-wider mb-1.5">Estado</label>
              <select value={selectedState} onChange={e => { setSelectedState(e.target.value); setSelectedCity(''); setCityName('') }}
                className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm bg-gray-50 focus:ring-2 focus:ring-brand-500 focus:border-brand-500 focus:bg-white transition-colors">
                <option value="">Selecione o estado</option>
                {states.map((s: any) => <option key={s.id} value={s.id}>{s.code} — {s.name}</option>)}
              </select>
            </div>
            {!isEstadual && (
              <div>
                <label className="block text-[11px] font-semibold text-gray-400 uppercase tracking-wider mb-1.5">Município</label>
                <select value={selectedCity} onChange={e => handleCityChange(e.target.value)} disabled={!selectedState}
                  className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm bg-gray-50 focus:ring-2 focus:ring-brand-500 focus:border-brand-500 focus:bg-white transition-colors disabled:opacity-50">
                  <option value="">Selecione o município</option>
                  {cities.map((c: any) => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Badge da seleção */}
      {selectedElectionData && ((isEstadual && uf) || (!isEstadual && cityName)) && (
        <div className="flex flex-wrap items-center gap-2">
          <div className="flex items-center gap-2 bg-brand-50 text-brand-700 px-3 py-1.5 rounded-xl">
            {isEstadual ? <MapPin className="w-4 h-4 flex-shrink-0" /> : <Building2 className="w-4 h-4 flex-shrink-0" />}
            <span className="text-sm font-semibold">{isEstadual ? stateName : cityName}</span>
            <span className="text-xs opacity-70">({uf})</span>
          </div>
          <div className={`flex items-center gap-2 px-3 py-1.5 rounded-xl ${isEstadual ? 'bg-amber-50 text-amber-700' : 'bg-gray-100 text-gray-700'}`}>
            <Vote className="w-4 h-4 flex-shrink-0" />
            <span className="text-sm font-medium">{selectedElectionData.year} — {isEstadual ? 'Estadual/Federal' : 'Municipal'}</span>
          </div>
        </div>
      )}

      {/* Loading skeleton */}
      {loading && (
        <div className="space-y-5 animate-pulse">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            {[1,2,3,4].map(i => <div key={i} className="h-20 bg-gray-100 rounded-2xl" />)}
          </div>
          <div className="bg-white rounded-2xl border border-gray-200 p-6 space-y-4">
            <div className="h-10 bg-gray-100 rounded-xl w-64" />
            <div className="space-y-3">
              {[1,2,3,4,5].map(i => <div key={i} className="h-12 bg-gray-50 rounded-xl" />)}
            </div>
          </div>
        </div>
      )}

      {/* Sem seleção */}
      {!loading && !(isEstadual ? uf : cityName) && (
        <div className="bg-white rounded-2xl border border-gray-200 p-16 text-center">
          <div className="w-16 h-16 bg-gray-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
            <Landmark className="w-8 h-8 text-gray-300" />
          </div>
          <h3 className="text-lg font-semibold text-gray-700">{isEstadual ? 'Selecione um estado' : 'Selecione uma cidade'}</h3>
          <p className="text-sm text-gray-400 mt-1 max-w-md mx-auto">Clique em "Filtros" acima para selecionar {isEstadual ? 'o estado' : 'o estado e município'}. Os dados eleitorais serão carregados automaticamente.</p>
          <button onClick={() => setShowFilters(true)} className="mt-4 px-5 py-2.5 bg-brand-500 text-white rounded-xl text-sm font-medium hover:bg-brand-600 transition-colors">
            {isEstadual ? 'Selecionar estado' : 'Selecionar cidade'}
          </button>
        </div>
      )}

      {/* Dashboard */}
      {d && (
        <>
          {/* Resumo inline */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            {[
              { v: zones.length, l: 'Zonas' },
              { v: totalSecoes.toLocaleString('pt-BR'), l: 'Seções' },
              { v: totalEleitores.toLocaleString('pt-BR'), l: 'Eleitores' },
              { v: comp?.aptos > 0 ? `${((comp.comparecimento / comp.aptos) * 100).toFixed(1)}%` : '—', l: 'Comparecimento' },
            ].map(s => (
              <div key={s.l} className="bg-white rounded-2xl border border-gray-200 p-4 text-center">
                <p className="text-2xl font-semibold text-gray-900">{s.v}</p>
                <p className="text-xs text-gray-500 mt-1">{s.l}</p>
              </div>
            ))}
          </div>

          {/* Seletor de cargo */}
          {(d.comparecimento_por_cargo || []).length > 1 && (
            <div className="flex items-center gap-1 bg-gray-100 rounded-lg p-1 w-fit">
              {(d.comparecimento_por_cargo || []).map((c: any) => (
                <button key={c.position_code} onClick={() => { setSelectedPosition(c.position_code); setActiveTab('geral'); setSelectedCandidates([]); setZoneResults(null); setBairroResults(null) }}
                  className={clsx('px-4 py-2 rounded-lg text-sm font-medium transition-colors', selectedPosition === c.position_code ? 'bg-white text-gray-900 shadow-xs' : 'text-gray-500 hover:text-gray-700')}>
                  {c.position_label}
                </button>
              ))}
            </div>
          )}

          {/* Tabs */}
          {(() => {
            const cargo = (d.comparecimento_por_cargo || []).find((c: any) => c.position_code === selectedPosition)
            if (!cargo) return null
            return (
              <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden">
                <div className="border-b border-gray-100 overflow-x-auto">
                  <div className="flex gap-6 px-6 min-w-max">
                    {[
                      { key: 'geral', label: 'Geral' },
                      { key: 'zonas', label: 'Por Zona' },
                      { key: 'bairros', label: isEstadual ? 'Por Cidade' : 'Por Bairro' },
                    ].map(tab => (
                      <button key={tab.key} onClick={() => {
                        setActiveTab(tab.key)
                        if (tab.key === 'zonas' && !zoneResults) {
                          // Usa as zonas já carregadas do dashboard (únicas, sem duplicata)
                          setZoneResults(zones)
                        }
                        if (tab.key === 'bairros' && !bairroResults) {
                          setBairroLoading(true)
                          if (isEstadual) {
                            // Estadual: carrega cidades
                            axios.get(buildApiUrl(ELECTION_ENDPOINTS.RESULTS_BY_CITY), { params: { uf, election_id: selectedElection } })
                              .then(r => setBairroResults(r.data.data))
                              .catch(() => {})
                              .finally(() => setBairroLoading(false))
                          } else {
                            // Municipal: carrega bairros
                            axios.get(buildApiUrl(ELECTION_ENDPOINTS.RESULTS_BY_NEIGHBORHOOD), { params: { city_id: selectedCity, election_id: selectedElection, position: selectedPosition } })
                              .then(r => setBairroResults(r.data.data))
                              .catch(() => {})
                              .finally(() => setBairroLoading(false))
                          }
                        }
                      }}
                        className={clsx('pb-2.5 pt-3 text-sm font-medium whitespace-nowrap transition-colors border-b-2 -mb-px',
                          activeTab === tab.key ? 'text-brand-600 border-brand-500' : 'text-gray-500 border-transparent hover:text-gray-700')}>
                        {tab.label}
                      </button>
                    ))}
                  </div>
                </div>

                <div className="p-4 sm:p-6">
                  {activeTab === 'geral' && (
                    <CargoTab cargo={cargo} data={d} fmt={fmt} onCandidateClick={openCandidateProfile}
                      selectedCandidates={selectedCandidates} onToggleCandidate={toggleCandidate} onCompare={compareCandidates} />
                  )}

                  {activeTab === 'zonas' && (
                    zoneLoading ? <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 text-brand-500 animate-spin" /></div>
                    : <ZonaResultTab zonas={zoneResults || []} onCandidateClick={openCandidateProfile} params={{ city_id: selectedCity, election_id: selectedElection, position: selectedPosition }} />
                  )}

                  {activeTab === 'bairros' && (
                    bairroLoading ? <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 text-brand-500 animate-spin" /></div>
                    : isEstadual
                      ? <CidadesTab cidades={bairroResults || []} electionId={selectedElection} position={selectedPosition} onCandidateClick={openCandidateProfile} />
                      : <BairroResultTab bairros={bairroResults || []} onCandidateClick={openCandidateProfile} params={{ city_id: selectedCity, election_id: selectedElection, position: selectedPosition }} />
                  )}
                </div>
              </div>
            )
          })()}
        </>
      )}

      {/* Painel do candidato */}
      <SlidePanel
        open={candidatePanelOpen}
        onClose={() => { setCandidatePanelOpen(false); setCandidatePanel(null) }}
        title={candidatePanel?.candidato?.nm_urna_candidato || 'Carregando...'}
        subtitle={candidatePanel ? `${candidatePanel.candidato?.cargo} — ${candidatePanel.candidato?.partido_sigla} (${candidatePanel.candidato?.nr_candidato})` : undefined}
        size="lg"
      >
        {candidateLoading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="w-6 h-6 text-brand-500 animate-spin" />
          </div>
        ) : candidatePanel ? (
          <div>
            {/* Resultado geral */}
            <div className="px-6 pt-5 pb-4 border-b border-gray-100">
              <div className="grid grid-cols-3 gap-3">
                <div className="bg-gray-50 rounded-xl p-3 text-center">
                  <p className="text-xl font-bold text-gray-900">{fmt(candidatePanel.resultado_municipio?.qt_votos)}</p>
                  <p className="text-[11px] text-gray-500">Votos</p>
                </div>
                <div className="bg-gray-50 rounded-xl p-3 text-center">
                  <p className="text-xl font-bold text-gray-900">{candidatePanel.resultado_municipio?.ranking || '—'}º</p>
                  <p className="text-[11px] text-gray-500">Posição</p>
                </div>
                <div className="bg-gray-50 rounded-xl p-3 text-center">
                  <p className="text-xl font-bold text-brand-600">{Number(candidatePanel.resultado_municipio?.pct_votos || 0).toFixed(1)}%</p>
                  <p className="text-[11px] text-gray-500">dos votos</p>
                </div>
              </div>
              {candidatePanel.resultado_municipio?.ds_sit_tot_turno?.includes('ELEITO') && !candidatePanel.resultado_municipio?.ds_sit_tot_turno?.includes('NÃO ELEITO') && (
                <div className="mt-3 text-center">
                  <span className="text-xs font-bold text-green-700 bg-green-50 px-3 py-1 rounded-full">ELEITO</span>
                </div>
              )}
            </div>

            {/* Tabs */}
            <div className="flex gap-6 border-b border-gray-200 px-6 pt-3">
              {[
                { key: 'bairros' as const, label: candidatePanel?.is_estadual ? 'Cidades' : 'Bairros' },
                { key: 'zonas' as const, label: 'Zonas' },
                { key: 'secoes' as const, label: 'Seções' },
              ].map(tab => (
                <button key={tab.key} onClick={() => setCandidateTab(tab.key)}
                  className={clsx('pb-2.5 text-sm font-medium transition-colors border-b-2 -mb-px',
                    candidateTab === tab.key ? 'text-brand-600 border-brand-500' : 'text-gray-500 border-transparent hover:text-gray-700')}>
                  {tab.label}
                </button>
              ))}
            </div>

            <div className="p-6">
              {/* Bairros / Cidades */}
              {candidateTab === 'bairros' && (
                candidatePanel?.is_estadual ? (
                  <CandidateCidadesPanel cidades={candidatePanel.resultado_cidades || []} bairros={candidatePanel.resultado_bairros || []} />
                ) : (
                  <div className="space-y-2">
                    {(candidatePanel.resultado_bairros || []).map((b: any, i: number) => {
                      const maxV = candidatePanel.resultado_bairros[0]?.votos || 1
                      return (
                        <div key={i} className="flex items-center gap-3 py-2">
                          <span className="text-xs font-mono text-gray-400 w-6 text-right">{i + 1}.</span>
                          <span className="text-sm font-medium text-gray-900 w-40 truncate">{b.nm_bairro}</span>
                          <div className="flex-1 h-5 bg-gray-100 rounded-full overflow-hidden">
                            <div className="h-full rounded-full bg-brand-500" style={{ width: `${Math.max((b.votos / maxV) * 100, 4)}%` }} />
                          </div>
                          <span className="text-sm font-bold text-gray-900 w-14 text-right">{Number(b.votos).toLocaleString('pt-BR')}</span>
                          <span className="text-xs text-gray-400 w-16 text-right">{b.secoes} seções</span>
                        </div>
                      )
                    })}
                    {(candidatePanel.resultado_bairros || []).length === 0 && <p className="text-sm text-gray-400 text-center py-8">Sem dados por bairro</p>}
                  </div>
                )
              )}

              {/* Zonas */}
              {candidateTab === 'zonas' && (
                <div className="space-y-2">
                  {(candidatePanel.resultado_zonas || []).map((z: any, i: number) => {
                    const maxV = candidatePanel.resultado_zonas[0]?.qt_votos || 1
                    return (
                      <div key={i} className="flex items-center gap-3 py-2">
                        <div className="w-10 h-10 bg-brand-50 text-brand-600 rounded-xl flex items-center justify-center font-bold text-sm flex-shrink-0">{z.nr_zona}</div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center justify-between mb-1">
                            <span className="text-sm font-medium text-gray-900">Zona {z.nr_zona}{z.nm_municipio ? ` — ${z.nm_municipio}` : ''}</span>
                            <div className="flex items-center gap-2">
                              <span className="text-sm font-bold text-gray-900">{Number(z.qt_votos).toLocaleString('pt-BR')}</span>
                              <span className="text-xs text-gray-400">{Number(z.pct_votos).toFixed(1)}%</span>
                            </div>
                          </div>
                          <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden">
                            <div className="h-full rounded-full bg-brand-500" style={{ width: `${(z.qt_votos / maxV) * 100}%` }} />
                          </div>
                        </div>
                      </div>
                    )
                  })}
                  {(candidatePanel.resultado_zonas || []).length === 0 && <p className="text-sm text-gray-400 text-center py-8">Sem dados por zona</p>}
                </div>
              )}

              {/* Seções */}
              {candidateTab === 'secoes' && (
                <div className="space-y-1">
                  <div className="grid grid-cols-[60px_60px_1fr_80px_60px] gap-2 text-[10px] font-semibold text-gray-400 uppercase tracking-wider pb-2 border-b border-gray-100 px-1">
                    <span>Zona</span><span>Seção</span><span>Local / Bairro</span><span className="text-right">Votos</span><span className="text-right">%</span>
                  </div>
                  {(candidatePanel.resultado_secoes || []).map((s: any, i: number) => (
                    <div key={i} className="grid grid-cols-[60px_60px_1fr_80px_60px] gap-2 py-2 px-1 hover:bg-gray-50 rounded-lg text-sm items-center">
                      <span className="font-medium text-gray-700">{s.nr_zona}</span>
                      <span className="font-medium text-gray-700">{s.nr_secao}</span>
                      <div className="min-w-0">
                        <p className="text-gray-900 truncate text-xs">{s.nm_local_votacao || '—'}</p>
                        {s.nm_bairro && <p className="text-[11px] text-gray-400 truncate">{s.nm_bairro}</p>}
                      </div>
                      <span className="text-right font-bold text-gray-900">{Number(s.qt_votos).toLocaleString('pt-BR')}</span>
                      <span className="text-right text-xs text-gray-500">{Number(s.pct_votos).toFixed(1)}%</span>
                    </div>
                  ))}
                  {(candidatePanel.resultado_secoes || []).length === 0 && <p className="text-sm text-gray-400 text-center py-8">Sem dados por seção</p>}
                </div>
              )}
            </div>
          </div>
        ) : null}
      </SlidePanel>

      {/* Painel de comparação */}
      <SlidePanel
        open={compareOpen}
        onClose={() => { setCompareOpen(false); setCompareData(null) }}
        title="Comparativo de Candidatos"
        size="lg"
      >
        {compareLoading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="w-6 h-6 text-brand-500 animate-spin" />
          </div>
        ) : compareData ? (
          <div>
            {/* Candidatos selecionados */}
            <div className="px-6 pt-5 pb-4 border-b border-gray-100">
              <div className="flex flex-wrap gap-2">
                {(compareData.candidates || []).map((c: any, i: number) => (
                  <span key={c.id} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium" style={{ backgroundColor: CHART_COLORS[i % CHART_COLORS.length] + '15', color: CHART_COLORS[i % CHART_COLORS.length] }}>
                    <span className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: CHART_COLORS[i % CHART_COLORS.length] }} />
                    {c.nm_urna_candidato} ({c.nr_candidato})
                  </span>
                ))}
              </div>
            </div>

            {/* Tabs */}
            <div className="flex gap-6 border-b border-gray-200 px-6 pt-3">
              {[
                { key: 'zonas' as const, label: 'Por Zona' },
                { key: 'bairros' as const, label: 'Por Bairro' },
              ].map(tab => (
                <button key={tab.key} onClick={() => setCompareTab(tab.key)}
                  className={clsx('pb-2.5 text-sm font-medium transition-colors border-b-2 -mb-px',
                    compareTab === tab.key ? 'text-brand-600 border-brand-500' : 'text-gray-500 border-transparent hover:text-gray-700')}>
                  {tab.label}
                </button>
              ))}
            </div>

            <div className="p-6">
              {compareTab === 'zonas' && (
                <div className="space-y-4">
                  {(compareData.zonas || []).map((z: any) => (
                    <div key={z.nr_zona} className="border border-gray-100 rounded-xl p-4">
                      <div className="flex items-center justify-between mb-3">
                        <span className="text-sm font-semibold text-gray-900">Zona {z.nr_zona}{z.nm_municipio ? ` — ${z.nm_municipio}` : ''}</span>
                        <span className="text-xs text-gray-400">{Number(z.total_eleitores).toLocaleString('pt-BR')} eleitores</span>
                      </div>
                      <div className="space-y-2">
                        {(compareData.candidates || []).map((c: any, ci: number) => {
                          const voto = z.candidatos.find((v: any) => v.candidate_id === c.id)
                          const maxZona = Math.max(...z.candidatos.map((v: any) => v.qt_votos), 1)
                          return (
                            <div key={c.id} className="flex items-center gap-3">
                              <span className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ backgroundColor: CHART_COLORS[ci % CHART_COLORS.length] }} />
                              <span className="text-xs text-gray-600 w-32 truncate">{c.nm_urna_candidato}</span>
                              <div className="flex-1 h-4 bg-gray-100 rounded-full overflow-hidden">
                                <div className="h-full rounded-full" style={{ width: `${voto ? (voto.qt_votos / maxZona) * 100 : 0}%`, backgroundColor: CHART_COLORS[ci % CHART_COLORS.length] }} />
                              </div>
                              <span className="text-sm font-bold text-gray-900 w-16 text-right">{voto ? Number(voto.qt_votos).toLocaleString('pt-BR') : '0'}</span>
                            </div>
                          )
                        })}
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {compareTab === 'bairros' && (
                <div className="space-y-4">
                  {(compareData.bairros || []).map((b: any) => (
                    <div key={b.nm_bairro} className="border border-gray-100 rounded-xl p-4">
                      <p className="text-sm font-semibold text-gray-900 mb-3">{b.nm_bairro}</p>
                      <div className="space-y-2">
                        {(compareData.candidates || []).map((c: any, ci: number) => {
                          const voto = b.candidatos.find((v: any) => v.candidate_id === c.id)
                          const maxBairro = Math.max(...b.candidatos.map((v: any) => v.qt_votos), 1)
                          return (
                            <div key={c.id} className="flex items-center gap-3">
                              <span className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ backgroundColor: CHART_COLORS[ci % CHART_COLORS.length] }} />
                              <span className="text-xs text-gray-600 w-32 truncate">{c.nm_urna_candidato}</span>
                              <div className="flex-1 h-4 bg-gray-100 rounded-full overflow-hidden">
                                <div className="h-full rounded-full" style={{ width: `${voto ? (voto.qt_votos / maxBairro) * 100 : 0}%`, backgroundColor: CHART_COLORS[ci % CHART_COLORS.length] }} />
                              </div>
                              <span className="text-sm font-bold text-gray-900 w-16 text-right">{voto ? Number(voto.qt_votos).toLocaleString('pt-BR') : '0'}</span>
                            </div>
                          )
                        })}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        ) : null}
      </SlidePanel>
    </div>
  )
}

// ── KPI CARD ──
function KpiCard({ icon: Icon, label, value, sub, color }: { icon: any; label: string; value: number; sub?: string; color: string }) {
  return (
    <div className="bg-white rounded-2xl border border-gray-200 p-5 hover:shadow-sm transition-shadow">
      <div className="flex items-center justify-between">
        <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ backgroundColor: color + '12' }}>
          <Icon className="w-5 h-5" style={{ color }} />
        </div>
      </div>
      <p className="text-2xl font-bold text-gray-900 mt-3">{Number(value).toLocaleString('pt-BR')}</p>
      <p className="text-xs text-gray-500 mt-0.5">{label}</p>
      {sub && <p className="text-[11px] font-medium mt-1" style={{ color }}>{sub}</p>}
    </div>
  )
}

// ── MINI KPI ──
function MiniKpi({ label, value, accent }: { label: string; value: string; accent?: string }) {
  const colors: Record<string, string> = { green: 'text-green-600', amber: 'text-amber-600', red: 'text-red-500', blue: 'text-blue-600' }
  return (
    <div className="bg-gray-50 rounded-xl px-3 py-2.5">
      <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider">{label}</p>
      <p className={`text-base font-bold mt-0.5 ${accent ? colors[accent] || 'text-gray-900' : 'text-gray-900'}`}>{value}</p>
    </div>
  )
}

// ── CARGO TAB ──
function CargoTab({ cargo, data, fmt, onCandidateClick, selectedCandidates, onToggleCandidate, onCompare }: {
  cargo: any; data: any; fmt: (n: any) => string; onCandidateClick: (id: number) => void
  selectedCandidates: number[]; onToggleCandidate: (id: number) => void; onCompare: () => void
}) {
  // Mapeia position_code → campo no data retornado pela API
  const positionFieldMap: Record<string, string> = {
    'PREFEITO': 'top_prefeito',
    'VEREADOR': 'top_vereadores',
    'GOVERNADOR': 'top_governador',
    'SENADOR': 'top_senador',
    'DEP_FEDERAL': 'top_dep_federal',
    'DEP_ESTADUAL': 'top_dep_estadual',
  }
  const candidates = data[positionFieldMap[cargo.position_code] || 'top_vereadores'] || []
  const maxVotes = candidates?.[0]?.qt_votos || 1

  const pieData = [
    { name: 'Válidos', value: Number(cargo.validos || 0), color: '#12b76a' },
    { name: 'Brancos', value: Number(cargo.brancos || 0), color: '#d1d5db' },
    { name: 'Nulos', value: Number(cargo.nulos || 0), color: '#f04438' },
    { name: 'Abstenções', value: Number(cargo.abstencoes || 0), color: '#f79009' },
  ].filter(d => d.value > 0)

  const showPartido = ['VEREADOR', 'DEP_FEDERAL', 'DEP_ESTADUAL'].includes(cargo.position_code)
  const partidoData = showPartido
    ? (data.votos_por_partido || []).slice(0, 15).map((p: any, i: number) => ({
        name: p.partido || '?', votos: Number(p.total_votos), fill: CHART_COLORS[i % CHART_COLORS.length],
      }))
    : []

  return (
    <div className="space-y-8">
      {/* Comparecimento */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2 sm:gap-3">
        <MiniKpi label="Aptos" value={fmt(cargo.aptos)} />
        <MiniKpi label="Compareceram" value={fmt(cargo.comparecimento)} accent="green" />
        <MiniKpi label="Abstenções" value={fmt(cargo.abstencoes)} accent="amber" />
        <MiniKpi label="Votos Válidos" value={fmt(cargo.validos)} accent="blue" />
        <MiniKpi label="Brancos" value={fmt(cargo.brancos)} />
        <MiniKpi label="Nulos" value={fmt(cargo.nulos)} accent="red" />
      </div>

      {/* Gráficos */}
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
        <div>
          <h3 className="text-sm font-semibold text-gray-800 mb-4">Distribuição dos Votos</h3>
          <div className="h-56 sm:h-72">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie data={pieData} cx="50%" cy="50%" innerRadius={65} outerRadius={100} paddingAngle={2} dataKey="value"
                  label={({ name, percent }) => `${name} ${(percent * 100).toFixed(1)}%`} labelLine={{ stroke: '#94a3b8', strokeWidth: 1 }}>
                  {pieData.map((entry, i) => <Cell key={i} fill={entry.color} />)}
                </Pie>
                <Tooltip formatter={(v: number) => v.toLocaleString('pt-BR')} contentStyle={{ borderRadius: '12px', border: '1px solid #e5e7eb', boxShadow: '0 4px 6px -1px rgba(0,0,0,0.1)' }} />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </div>

        {partidoData.length > 0 && (
          <div>
            <h3 className="text-sm font-semibold text-gray-800 mb-4">Votos por Partido</h3>
            <div className="h-56 sm:h-72">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={partidoData} layout="vertical" margin={{ left: 5 }} barCategoryGap={4}>
                  <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke="#f1f5f9" />
                  <XAxis type="number" tickFormatter={(v: number) => v >= 1000 ? `${(v / 1000).toFixed(0)}k` : String(v)} tick={{ fontSize: 11, fill: '#94a3b8' }} axisLine={false} />
                  <YAxis type="category" dataKey="name" width={45} tick={{ fontSize: 11, fontWeight: 600, fill: '#475569' }} axisLine={false} tickLine={false} />
                  <Tooltip formatter={(v: number) => v.toLocaleString('pt-BR')} contentStyle={{ borderRadius: '12px', border: '1px solid #e5e7eb' }} />
                  <Bar dataKey="votos" radius={[0, 6, 6, 0]} barSize={18}>
                    {partidoData.map((entry: any, i: number) => <Cell key={i} fill={entry.fill} />)}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        )}
      </div>

      {/* Candidatos */}
      {candidates?.length > 0 && (
        <div>
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-sm font-semibold text-gray-800">
              {cargo.position_code === 'PREFEITO' ? 'Resultado' : 'Candidatos'} — {cargo.position_label}
            </h3>
            {selectedCandidates.length >= 2 && (
              <button onClick={onCompare}
                className="px-4 py-2 bg-brand-500 text-white text-sm font-medium rounded-xl hover:bg-brand-600 transition-colors">
                Comparar ({selectedCandidates.length})
              </button>
            )}
          </div>
          <div className="space-y-2">
            {candidates.map((c: any, i: number) => (
              <div key={i} className="flex items-center gap-2">
                <input type="checkbox" checked={selectedCandidates.includes(c.candidate_id)}
                  onChange={() => onToggleCandidate(c.candidate_id)}
                  className="w-4 h-4 rounded border-gray-300 text-brand-500 focus:ring-brand-500 flex-shrink-0" />
                <div className="flex-1">
                  <CandidateBar {...c} maxVotes={maxVotes} compact={cargo.position_code !== 'PREFEITO'} onClick={() => onCandidateClick(c.candidate_id)} />
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}

// ── OVERVIEW TAB (não usado mais, mantido por referência) ──
function OverviewTab({ data }: { data: any }) {
  const comp = data.comparecimento || {}

  const pieData = [
    { name: 'Válidos', value: Number(comp.validos || 0), color: '#12b76a' },
    { name: 'Brancos', value: Number(comp.brancos || 0), color: '#d1d5db' },
    { name: 'Nulos', value: Number(comp.nulos || 0), color: '#f04438' },
    { name: 'Abstenções', value: Number(comp.abstencoes || 0), color: '#f79009' },
  ].filter(d => d.value > 0)

  const partidoData = (data.votos_por_partido || []).slice(0, 15).map((p: any, i: number) => ({
    name: p.partido || '?',
    votos: Number(p.total_votos),
    fill: CHART_COLORS[i % CHART_COLORS.length],
  }))

  return (
    <div className="space-y-8">
      {/* Charts */}
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
        {/* Pie */}
        <div>
          <h3 className="text-sm font-semibold text-gray-800 mb-4">Distribuição dos Votos</h3>
          <div className="h-56 sm:h-72">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie data={pieData} cx="50%" cy="50%" innerRadius={65} outerRadius={100} paddingAngle={2} dataKey="value"
                  label={({ name, percent }) => `${name} ${(percent * 100).toFixed(1)}%`} labelLine={{ stroke: '#94a3b8', strokeWidth: 1 }}>
                  {pieData.map((entry, i) => <Cell key={i} fill={entry.color} />)}
                </Pie>
                <Tooltip formatter={(v: number) => v.toLocaleString('pt-BR')} contentStyle={{ borderRadius: '12px', border: '1px solid #e5e7eb', boxShadow: '0 4px 6px -1px rgba(0,0,0,0.1)' }} />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Bar partidos */}
        <div>
          <h3 className="text-sm font-semibold text-gray-800 mb-4">Votos por Partido (Vereador)</h3>
          <div className="h-56 sm:h-72">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={partidoData} layout="vertical" margin={{ left: 5 }} barCategoryGap={4}>
                <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke="#f1f5f9" />
                <XAxis type="number" tickFormatter={(v: number) => v >= 1000 ? `${(v / 1000).toFixed(0)}k` : String(v)} tick={{ fontSize: 11, fill: '#94a3b8' }} axisLine={false} />
                <YAxis type="category" dataKey="name" width={45} tick={{ fontSize: 11, fontWeight: 600, fill: '#475569' }} axisLine={false} tickLine={false} />
                <Tooltip formatter={(v: number) => v.toLocaleString('pt-BR')} contentStyle={{ borderRadius: '12px', border: '1px solid #e5e7eb' }} />
                <Bar dataKey="votos" radius={[0, 6, 6, 0]} barSize={18}>
                  {partidoData.map((entry: any, i: number) => <Cell key={i} fill={entry.fill} />)}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      {/* Resultados por cargo — dinâmico */}
      {[
        { key: 'top_governador', label: 'Governador', compact: false },
        { key: 'top_prefeito', label: 'Prefeito', compact: false },
        { key: 'top_senador', label: 'Senador', compact: false },
        { key: 'top_dep_federal', label: 'Top 20 — Deputados Federais', compact: true },
        { key: 'top_dep_estadual', label: 'Top 20 — Deputados Estaduais', compact: true },
        { key: 'top_vereadores', label: 'Top 20 — Vereadores', compact: true },
      ].filter(item => data[item.key]?.length > 0).map(item => (
        <div key={item.key}>
          <h3 className="text-sm font-semibold text-gray-800 mb-4">Resultado — {item.label}</h3>
          <div className={item.compact ? 'space-y-2' : 'space-y-3'}>
            {data[item.key].slice(0, 20).map((c: any, i: number) => (
              <CandidateBar key={i} {...c} maxVotes={data[item.key][0]?.qt_votos} compact={item.compact} />
            ))}
          </div>
        </div>
      ))}
    </div>
  )
}

// ── ZONES TAB ──
function ZonesTab({ zones, navigate }: { zones: any[]; navigate: any }) {
  const maxEleitores = Math.max(...zones.map((z: any) => Number(z.total_eleitores)), 1)

  return (
    <div className="space-y-4">
      {zones.map((z: any) => {
        const pct = (Number(z.total_eleitores) / maxEleitores) * 100
        return (
          <div key={z.id} className="group border border-gray-100 rounded-xl p-4 hover:border-brand-200 hover:bg-brand-50/20 transition-all cursor-pointer"
            onClick={() => navigate(`/reports/elections/zone/${z.id}`)}>
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-brand-50 text-brand-600 rounded-xl flex items-center justify-center font-bold text-sm">
                  {z.nr_zona}
                </div>
                <div>
                  <p className="font-semibold text-gray-900">Zona {z.nr_zona}{z.nm_municipio ? ` — ${z.nm_municipio}` : ''}</p>
                  <p className="text-xs text-gray-500">{z.total_locais_votacao} locais de votação</p>
                </div>
              </div>
              <div className="text-right">
                <p className="text-lg font-bold text-gray-900">{Number(z.total_eleitores).toLocaleString('pt-BR')}</p>
                <p className="text-xs text-gray-500">{z.total_secoes} seções</p>
              </div>
            </div>
            <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
              <div className="h-full bg-brand-500 rounded-full transition-all group-hover:bg-brand-600" style={{ width: `${pct}%` }} />
            </div>
            {z.bairros?.length > 0 && (
              <div className="flex flex-wrap gap-1.5 mt-3">
                {z.bairros.slice(0, 6).map((b: string, i: number) => (
                  <span key={i} className="text-[10px] font-medium bg-gray-100 text-gray-600 px-2 py-0.5 rounded-md">{b}</span>
                ))}
                {z.bairros.length > 6 && <span className="text-[10px] text-gray-400">+{z.bairros.length - 6} bairros</span>}
              </div>
            )}
          </div>
        )
      })}

      {/* Totais */}
      <div className="border-t-2 border-gray-200 pt-4 flex items-center justify-between px-4">
        <span className="font-bold text-gray-700">Total: {zones.length} zonas</span>
        <div className="flex gap-6">
          <span className="text-sm text-gray-600"><span className="font-bold text-gray-900">{zones.reduce((s: number, z: any) => s + Number(z.total_secoes), 0).toLocaleString('pt-BR')}</span> seções</span>
          <span className="text-sm text-gray-600"><span className="font-bold text-gray-900">{zones.reduce((s: number, z: any) => s + Number(z.total_eleitores), 0).toLocaleString('pt-BR')}</span> eleitores</span>
        </div>
      </div>
    </div>
  )
}

// ── BAIRROS TAB ──
function BairrosTab({ neighborhoods }: { neighborhoods: any[] }) {
  const max = Number(neighborhoods[0]?.total_eleitores || 1)
  const total = neighborhoods.reduce((s: number, n: any) => s + Number(n.total_eleitores), 0)

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-sm font-semibold text-gray-800">{neighborhoods.length} bairros mapeados</h3>
        <span className="text-sm text-gray-500">{total.toLocaleString('pt-BR')} eleitores</span>
      </div>

      {neighborhoods.map((b: any, i: number) => {
        const pct = (Number(b.total_eleitores) / total * 100).toFixed(1)
        const barPct = (Number(b.total_eleitores) / max) * 100
        return (
          <div key={i} className="flex items-center gap-3 py-2.5 px-3 rounded-xl hover:bg-gray-50 transition-colors">
            <span className="text-xs font-mono text-gray-400 w-7 text-right flex-shrink-0">{i + 1}.</span>
            <div className="flex-1 min-w-0">
              <div className="flex items-center justify-between mb-1">
                <span className="text-sm font-medium text-gray-900 truncate">{b.nm_bairro}</span>
                <div className="flex items-center gap-3 ml-3 flex-shrink-0">
                  <span className="text-xs text-gray-400">{pct}%</span>
                  <span className="text-sm font-bold text-gray-900 w-20 text-right">{Number(b.total_eleitores).toLocaleString('pt-BR')}</span>
                </div>
              </div>
              <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden">
                <div className="h-full rounded-full bg-gradient-to-r from-brand-400 to-brand-600" style={{ width: `${barPct}%` }} />
              </div>
              <div className="flex gap-4 mt-1">
                <span className="text-[10px] text-gray-400">{b.total_secoes} seções</span>
                <span className="text-[10px] text-gray-400">{b.total_locais} locais</span>
                <span className="text-[10px] text-gray-400">Zonas: {b.zonas}</span>
              </div>
            </div>
          </div>
        )
      })}
    </div>
  )
}

// ── CANDIDATOS TAB ──
function CandidatosTab({ data }: { data: any }) {
  const cargos = [
    { key: 'top_governador', label: 'Governador', icon: Landmark, color: 'amber', compact: false },
    { key: 'top_prefeito', label: 'Prefeito', icon: Landmark, color: 'amber', compact: false },
    { key: 'top_senador', label: 'Senador', icon: Award, color: 'purple', compact: false },
    { key: 'top_dep_federal', label: 'Deputados Federais', icon: Users, color: 'blue', compact: true },
    { key: 'top_dep_estadual', label: 'Deputados Estaduais', icon: Users, color: 'brand', compact: true },
    { key: 'top_vereadores', label: 'Vereadores', icon: Users, color: 'brand', compact: true },
  ].filter(c => data[c.key]?.length > 0)

  return (
    <div className="space-y-8">
      {cargos.map(cargo => {
        const Icon = cargo.icon
        const colorMap: Record<string, string> = { amber: 'bg-amber-50 text-amber-600', purple: 'bg-purple-50 text-purple-600', blue: 'bg-blue-50 text-blue-600', brand: 'bg-brand-50 text-brand-600' }
        return (
          <div key={cargo.key}>
            <div className="flex items-center gap-2 mb-4">
              <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${colorMap[cargo.color] || colorMap.brand}`}>
                <Icon className="w-4 h-4" />
              </div>
              <h3 className="text-sm font-semibold text-gray-800">{cargo.label}</h3>
            </div>
            <div className={cargo.compact ? 'space-y-2' : 'space-y-3'}>
              {data[cargo.key].slice(0, 30).map((c: any, i: number) => (
                <CandidateBar key={i} {...c} maxVotes={data[cargo.key][0]?.qt_votos} compact={cargo.compact} />
              ))}
            </div>
          </div>
        )
      })}
    </div>
  )
}

// ── CANDIDATE BAR ──
// ── ZONA RESULT TAB ──
function ZonaResultTab({ zonas, onCandidateClick, params }: { zonas: any[]; onCandidateClick: (id: number) => void; params: { city_id: string; election_id: number | null; position: string } }) {
  const [expandedZona, setExpandedZona] = useState<number | null>(null)
  const [candidatos, setCandidatos] = useState<any[]>([])
  const [loadingDetail, setLoadingDetail] = useState(false)

  function toggleZona(zone: any) {
    const key = zone.id || zone.nr_zona
    if (expandedZona === key) { setExpandedZona(null); return }
    setExpandedZona(key)
    setLoadingDetail(true)
    setCandidatos([])
    // Estadual (sem city_id): usa nr_zona. Municipal: usa zone_id direto
    const queryParams = zone.municipios
      ? { ...params, nr_zona: zone.nr_zona, uf: zone.sg_uf }
      : { ...params, zone_id: zone.id }
    axios.get(buildApiUrl(ELECTION_ENDPOINTS.RESULTS_BY_ZONE), { params: queryParams })
      .then(r => setCandidatos(r.data.data || []))
      .catch(() => {})
      .finally(() => setLoadingDetail(false))
  }

  return (
    <div className="space-y-3">
      {zonas.map((z: any) => {
        const zoneId = z.id || z.zone_id
        const isExpanded = expandedZona === zoneId
        const maxVotos = candidatos[0]?.qt_votos || 1
        return (
          <div key={zoneId} className="border border-gray-100 rounded-xl overflow-hidden">
            <button onClick={() => toggleZona(z)}
              className="w-full flex items-center justify-between p-4 hover:bg-gray-50 transition-colors">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-brand-50 text-brand-600 rounded-xl flex items-center justify-center font-bold text-sm flex-shrink-0">{z.nr_zona}</div>
                <div className="text-left">
                  <p className="font-semibold text-gray-900 text-sm">Zona {z.nr_zona}{z.nm_municipio ? ` — ${z.nm_municipio}` : ''}</p>
                  <p className="text-xs text-gray-500">{Number(z.total_eleitores).toLocaleString('pt-BR')} eleitores · {z.total_secoes} seções{z.municipios ? ` · ${z.municipios}` : ''}</p>
                </div>
              </div>
              <ChevronDown className={clsx('w-4 h-4 text-gray-400 transition-transform', isExpanded && 'rotate-180')} />
            </button>
            {isExpanded && (
              <div className="px-4 pb-4 space-y-2 border-t border-gray-50 pt-3">
                {loadingDetail ? <div className="flex justify-center py-4"><Loader2 className="w-5 h-5 text-brand-500 animate-spin" /></div>
                : candidatos.map((c: any, i: number) => (
                  <div key={c.candidate_id} onClick={() => onCandidateClick(c.candidate_id)}
                    className="flex items-center gap-2 sm:gap-3 py-1.5 cursor-pointer hover:bg-gray-50 rounded-lg px-2 -mx-2">
                    <span className="text-xs font-bold text-gray-400 w-6 text-right flex-shrink-0">{c.ranking}º</span>
                    <span className="text-xs sm:text-sm font-medium text-gray-900 w-28 sm:w-36 truncate">{c.nm_urna_candidato}</span>
                    <span className="text-[10px] text-gray-400 flex-shrink-0">{c.nr_candidato}</span>
                    {c.partido && <span className="text-[10px] font-semibold text-gray-500 bg-gray-100 px-1.5 py-0.5 rounded flex-shrink-0 hidden sm:inline">{c.partido}</span>}
                    <div className="flex-1 h-3 bg-gray-100 rounded-full overflow-hidden">
                      <div className="h-full rounded-full bg-brand-500" style={{ width: `${(c.qt_votos / maxVotos) * 100}%` }} />
                    </div>
                    <span className="text-xs sm:text-sm font-bold text-gray-900 w-14 text-right">{Number(c.qt_votos).toLocaleString('pt-BR')}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        )
      })}
      {zonas.length === 0 && <p className="text-sm text-gray-400 text-center py-8">Sem dados por zona</p>}
    </div>
  )
}

// ── CIDADES NO PAINEL LATERAL DO CANDIDATO (estadual) ──
function CandidateCidadesPanel({ cidades, bairros }: { cidades: any[]; bairros: any[] }) {
  const [expandedCity, setExpandedCity] = useState<string | null>(null)

  const maxV = cidades[0]?.votos || 1

  // Filtra bairros pela cidade expandida (bairros não têm city_id, mas podemos usar nm_bairro)
  // Por enquanto mostra todos — idealmente a API retornaria bairros por cidade

  return (
    <div className="space-y-2">
      {cidades.map((c: any, i: number) => {
        const barW = (Number(c.votos) / Number(maxV)) * 100
        const isExpanded = expandedCity === c.cidade
        return (
          <div key={c.city_id || i} className="border border-gray-100 rounded-lg overflow-hidden">
            <button onClick={() => setExpandedCity(isExpanded ? null : c.cidade)}
              className="w-full flex items-center gap-3 py-2.5 px-3 hover:bg-gray-50 transition-colors text-left">
              <span className="text-xs font-mono text-gray-400 w-5 text-right">{i + 1}.</span>
              <div className="flex-1 min-w-0">
                <div className="flex items-center justify-between mb-0.5">
                  <span className="text-sm font-medium text-gray-900 truncate">{c.cidade}</span>
                  <span className="text-sm font-bold text-gray-900 ml-2">{Number(c.votos).toLocaleString('pt-BR')}</span>
                </div>
                <div className="h-1 bg-gray-100 rounded-full overflow-hidden">
                  <div className="h-full rounded-full bg-brand-500" style={{ width: `${barW}%` }} />
                </div>
              </div>
              <ChevronDown className={clsx('w-3.5 h-3.5 text-gray-300 transition-transform flex-shrink-0', isExpanded && 'rotate-180')} />
            </button>
            {isExpanded && (
              <div className="px-3 pb-2 pt-1 border-t border-gray-50 bg-gray-50/50">
                <p className="text-[10px] font-semibold text-gray-400 uppercase mb-1.5">Bairros desta cidade</p>
                <div className="space-y-0.5 max-h-40 overflow-y-auto">
                  {bairros.filter(b => b.votos > 0).slice(0, 20).map((b: any, j: number) => (
                    <div key={j} className="flex items-center justify-between py-1">
                      <span className="text-xs text-gray-700 truncate">{b.nm_bairro}</span>
                      <span className="text-xs font-bold text-gray-900 ml-2">{Number(b.votos).toLocaleString('pt-BR')}</span>
                    </div>
                  ))}
                  {bairros.length === 0 && <p className="text-xs text-gray-400 py-2">Sem bairros mapeados</p>}
                </div>
              </div>
            )}
          </div>
        )
      })}
      {cidades.length === 0 && <p className="text-sm text-gray-400 text-center py-8">Sem dados por cidade</p>}
    </div>
  )
}

// ── CIDADES TAB (estadual) ──
function CidadesTab({ cidades, electionId, position, onCandidateClick }: {
  cidades: any[]; electionId: number | null; position: string; onCandidateClick: (id: number) => void
}) {
  const [expandedCity, setExpandedCity] = useState<number | null>(null)
  const [cityBairros, setCityBairros] = useState<any[]>([])
  const [cityCandidatos, setCityCandidatos] = useState<any[]>([])
  const [loadingCity, setLoadingCity] = useState(false)
  const [cityTab, setCityTab] = useState<'candidatos' | 'bairros'>('candidatos')
  const [expandedBairro, setExpandedBairro] = useState<string | null>(null)
  const [bairroCandidatos, setBairroCandidatos] = useState<any[]>([])
  const [loadingBairro, setLoadingBairro] = useState(false)

  const max = Number(cidades[0]?.total_eleitores || 1)
  const total = cidades.reduce((s: number, c: any) => s + Number(c.total_eleitores || 0), 0)

  function toggleCity(cityId: number) {
    if (expandedCity === cityId) { setExpandedCity(null); return }
    setExpandedCity(cityId)
    setCityTab('candidatos')
    setLoadingCity(true)
    setCityCandidatos([])
    setCityBairros([])

    // Busca candidatos do cargo selecionado nesta cidade, ordenados por votos
    Promise.all([
      axios.get(buildApiUrl(ELECTION_ENDPOINTS.RESULTS_CANDIDATES), {
        params: { city_id: cityId, election_id: electionId, position, top: 50 }
      }).catch(() => ({ data: { data: [] } })),
      axios.get(buildApiUrl(ELECTION_ENDPOINTS.ZONES), { params: { city_id: cityId } })
        .catch(() => ({ data: { data: [] } })),
    ]).then(([candRes, zonesRes]) => {
      setCityCandidatos(candRes.data.data || [])
      // Extrai bairros das zonas
      const bairrosSet = new Set<string>()
      ;(zonesRes.data.data || []).forEach((z: any) => {
        const b = typeof z.bairros === 'string' ? JSON.parse(z.bairros) : z.bairros || []
        b.forEach((name: string) => bairrosSet.add(name))
      })
      setCityBairros(Array.from(bairrosSet).sort().map(name => ({ nm_bairro: name })))
    }).finally(() => setLoadingCity(false))
  }

  // Recarrega quando muda o cargo
  useEffect(() => {
    if (expandedCity) {
      setLoadingCity(true)
      axios.get(buildApiUrl(ELECTION_ENDPOINTS.RESULTS_CANDIDATES), {
        params: { city_id: expandedCity, election_id: electionId, position, top: 50 }
      }).then(r => setCityCandidatos(r.data.data || []))
        .catch(() => {})
        .finally(() => setLoadingCity(false))
    }
  }, [position])

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between mb-4 px-3">
        <h3 className="text-sm font-semibold text-gray-800">{cidades.length} cidades</h3>
        <span className="text-sm text-gray-500">{total.toLocaleString('pt-BR')} eleitores</span>
      </div>
      {cidades.map((c: any, i: number) => {
        const barPct = (Number(c.total_eleitores) / max) * 100
        const isExpanded = expandedCity === c.city_id
        return (
          <div key={c.city_id || i} className="border border-gray-100 rounded-xl overflow-hidden">
            <button onClick={() => toggleCity(c.city_id)}
              className="w-full flex items-center gap-3 py-3 px-4 hover:bg-gray-50 transition-colors text-left">
              <span className="text-xs font-mono text-gray-400 w-6 text-right flex-shrink-0">{i + 1}.</span>
              <div className="flex-1 min-w-0">
                <div className="flex items-center justify-between mb-1">
                  <span className="text-sm font-semibold text-gray-900">{c.cidade}</span>
                  <div className="flex items-center gap-3 ml-3 flex-shrink-0">
                    {c.pct_comparecimento > 0 && <span className="text-[10px] text-green-600 font-medium">{c.pct_comparecimento}% comp.</span>}
                    <span className="text-sm font-bold text-gray-900">{Number(c.total_eleitores).toLocaleString('pt-BR')}</span>
                  </div>
                </div>
                <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden">
                  <div className="h-full rounded-full bg-gradient-to-r from-brand-400 to-brand-600" style={{ width: `${barPct}%` }} />
                </div>
                <div className="flex gap-4 mt-1">
                  <span className="text-[10px] text-gray-400">{c.total_zonas} zonas</span>
                  <span className="text-[10px] text-gray-400">{Number(c.total_secoes).toLocaleString('pt-BR')} seções</span>
                </div>
              </div>
              <ChevronDown className={clsx('w-4 h-4 text-gray-400 transition-transform flex-shrink-0', isExpanded && 'rotate-180')} />
            </button>

            {isExpanded && (
              <div className="border-t border-gray-100">
                {loadingCity ? (
                  <div className="flex justify-center py-6"><Loader2 className="w-5 h-5 text-brand-500 animate-spin" /></div>
                ) : (
                  <>
                    <div className="flex gap-4 px-4 pt-3 border-b border-gray-50">
                      {[
                        { key: 'candidatos' as const, label: 'Candidatos' },
                        { key: 'bairros' as const, label: `Bairros (${cityBairros.length})` },
                      ].map(tab => (
                        <button key={tab.key} onClick={() => setCityTab(tab.key)}
                          className={clsx('pb-2 text-xs font-medium border-b-2 transition-colors',
                            cityTab === tab.key ? 'text-brand-600 border-brand-500' : 'text-gray-400 border-transparent')}>
                          {tab.label}
                        </button>
                      ))}
                    </div>

                    <div className="px-4 py-3">
                      {cityTab === 'candidatos' && (
                        <div className="space-y-1.5 max-h-96 overflow-y-auto">
                          {cityCandidatos.length === 0 && <p className="text-xs text-gray-400 text-center py-4">Sem resultados para este cargo</p>}
                          {cityCandidatos.map((cand: any, j: number) => {
                            const maxV = cityCandidatos[0]?.qt_votos || 1
                            const barW = (Number(cand.qt_votos) / Number(maxV)) * 100
                            const isElected = cand.ds_sit_tot_turno?.includes('ELEITO') && !cand.ds_sit_tot_turno?.includes('NÃO')
                            return (
                              <div key={cand.candidate_id || j}
                                onClick={() => cand.candidate_id && onCandidateClick(cand.candidate_id)}
                                className="py-2 px-2 rounded-lg hover:bg-gray-50 cursor-pointer -mx-2">
                                <div className="flex items-center gap-2">
                                  <span className={clsx('text-[10px] font-bold w-5 text-center',
                                    j === 0 ? 'text-amber-600' : j <= 2 ? 'text-brand-600' : 'text-gray-400')}>
                                    {j + 1}º
                                  </span>
                                  <span className="text-sm font-medium text-gray-900 truncate flex-1">{cand.nm_urna_candidato}</span>
                                  {cand.partido && <span className="text-[10px] text-gray-500 bg-gray-100 px-1.5 py-0.5 rounded">{cand.partido}</span>}
                                  {isElected && <span className="text-[10px] font-bold text-green-700 bg-green-50 px-1.5 py-0.5 rounded-full">ELEITO</span>}
                                  <span className="text-sm font-bold text-gray-900">{Number(cand.qt_votos).toLocaleString('pt-BR')}</span>
                                  {cand.pct_votos && <span className="text-[10px] text-gray-400 w-12 text-right">{Number(cand.pct_votos).toFixed(1)}%</span>}
                                </div>
                                <div className="ml-7 mt-1 h-1 bg-gray-100 rounded-full overflow-hidden">
                                  <div className={`h-full rounded-full ${isElected ? 'bg-green-500' : 'bg-brand-400'}`} style={{ width: `${barW}%` }} />
                                </div>
                              </div>
                            )
                          })}
                        </div>
                      )}

                      {cityTab === 'bairros' && (
                        <div className="space-y-1.5 max-h-96 overflow-y-auto">
                          {cityBairros.length === 0 && <p className="text-xs text-gray-400 text-center py-4">Sem bairros mapeados</p>}
                          {cityBairros.map((b: any, j: number) => {
                            const isBairroExpanded = expandedBairro === b.nm_bairro
                            return (
                              <div key={j} className="border border-gray-50 rounded-lg overflow-hidden">
                                <button onClick={() => {
                                  if (isBairroExpanded) { setExpandedBairro(null); return }
                                  setExpandedBairro(b.nm_bairro)
                                  setLoadingBairro(true)
                                  setBairroCandidatos([])
                                  axios.get(buildApiUrl(ELECTION_ENDPOINTS.RESULTS_BY_NEIGHBORHOOD), {
                                    params: { city_id: expandedCity, election_id: electionId, position, neighborhood: b.nm_bairro }
                                  }).then(r => setBairroCandidatos(r.data.data || []))
                                    .catch(() => {})
                                    .finally(() => setLoadingBairro(false))
                                }}
                                  className="w-full flex items-center justify-between py-2 px-2 hover:bg-gray-50 transition-colors text-left">
                                  <div className="flex items-center gap-2">
                                    <MapPin className="w-3 h-3 text-gray-300 flex-shrink-0" />
                                    <span className="text-sm font-medium text-gray-700">{b.nm_bairro}</span>
                                  </div>
                                  <ChevronDown className={clsx('w-3.5 h-3.5 text-gray-300 transition-transform', isBairroExpanded && 'rotate-180')} />
                                </button>
                                {isBairroExpanded && (
                                  <div className="px-2 pb-2 pt-1 border-t border-gray-50">
                                    {loadingBairro ? (
                                      <div className="flex justify-center py-3"><Loader2 className="w-4 h-4 text-brand-500 animate-spin" /></div>
                                    ) : bairroCandidatos.length === 0 ? (
                                      <p className="text-xs text-gray-400 text-center py-2">Sem votos neste bairro</p>
                                    ) : (
                                      <div className="space-y-1">
                                        {bairroCandidatos.map((cand: any, k: number) => {
                                          const maxV = bairroCandidatos[0]?.qt_votos || 1
                                          const barW = (Number(cand.qt_votos) / Number(maxV)) * 100
                                          return (
                                            <div key={cand.candidate_id || k}
                                              onClick={() => cand.candidate_id && onCandidateClick(cand.candidate_id)}
                                              className="py-1 px-1 rounded hover:bg-gray-50 cursor-pointer">
                                              <div className="flex items-center gap-1.5">
                                                <span className="text-[10px] font-bold text-gray-400 w-4 text-right">{k + 1}º</span>
                                                <span className="text-xs font-medium text-gray-900 truncate flex-1">{cand.nm_urna_candidato}</span>
                                                {cand.partido && <span className="text-[9px] text-gray-400 bg-gray-100 px-1 rounded">{cand.partido}</span>}
                                                <span className="text-xs font-bold text-gray-900">{Number(cand.qt_votos).toLocaleString('pt-BR')}</span>
                                              </div>
                                              <div className="ml-5 mt-0.5 h-0.5 bg-gray-100 rounded-full overflow-hidden">
                                                <div className="h-full rounded-full bg-brand-400" style={{ width: `${barW}%` }} />
                                              </div>
                                            </div>
                                          )
                                        })}
                                      </div>
                                    )}
                                  </div>
                                )}
                              </div>
                            )
                          })}
                        </div>
                      )}
                    </div>
                  </>
                )}
              </div>
            )}
          </div>
        )
      })}
    </div>
  )
}

// ── BAIRRO RESULT TAB (municipal) ──
function BairroResultTab({ bairros, onCandidateClick, params }: { bairros: any[]; onCandidateClick: (id: number) => void; params: { city_id: string; election_id: number | null; position: string } }) {
  const [expandedBairro, setExpandedBairro] = useState<string | null>(null)
  const [candidatos, setCandidatos] = useState<any[]>([])
  const [loadingDetail, setLoadingDetail] = useState(false)

  function toggleBairro(nome: string) {
    if (expandedBairro === nome) { setExpandedBairro(null); return }
    setExpandedBairro(nome)
    setLoadingDetail(true)
    setCandidatos([])
    axios.get(buildApiUrl(ELECTION_ENDPOINTS.RESULTS_BY_NEIGHBORHOOD), { params: { ...params, neighborhood: nome } })
      .then(r => setCandidatos(r.data.data || []))
      .catch(() => {})
      .finally(() => setLoadingDetail(false))
  }

  return (
    <div className="space-y-3">
      {bairros.map((b: any) => {
        const isExpanded = expandedBairro === b.nm_bairro
        const maxVotos = candidatos[0]?.qt_votos || 1
        return (
          <div key={b.nm_bairro} className="border border-gray-100 rounded-xl overflow-hidden">
            <button onClick={() => toggleBairro(b.nm_bairro)}
              className="w-full flex items-center justify-between p-4 hover:bg-gray-50 transition-colors">
              <div className="text-left">
                <p className="font-semibold text-gray-900 text-sm">{b.nm_bairro}</p>
                <p className="text-xs text-gray-500">{Number(b.total_eleitores).toLocaleString('pt-BR')} eleitores &middot; {b.total_secoes} seções</p>
              </div>
              <ChevronDown className={clsx('w-4 h-4 text-gray-400 transition-transform', isExpanded && 'rotate-180')} />
            </button>
            {isExpanded && (
              <div className="px-4 pb-4 space-y-2 border-t border-gray-50 pt-3">
                {loadingDetail ? <div className="flex justify-center py-4"><Loader2 className="w-5 h-5 text-brand-500 animate-spin" /></div>
                : candidatos.map((c: any, i: number) => (
                  <div key={c.candidate_id} onClick={() => onCandidateClick(c.candidate_id)}
                    className="flex items-center gap-2 sm:gap-3 py-1.5 cursor-pointer hover:bg-gray-50 rounded-lg px-2 -mx-2">
                    <span className="text-xs font-bold text-gray-400 w-6 text-right flex-shrink-0">{i + 1}º</span>
                    <span className="text-xs sm:text-sm font-medium text-gray-900 w-28 sm:w-36 truncate">{c.nm_urna_candidato}</span>
                    <span className="text-[10px] text-gray-400 flex-shrink-0">{c.nr_candidato}</span>
                    {c.partido && <span className="text-[10px] font-semibold text-gray-500 bg-gray-100 px-1.5 py-0.5 rounded flex-shrink-0 hidden sm:inline">{c.partido}</span>}
                    <div className="flex-1 h-3 bg-gray-100 rounded-full overflow-hidden">
                      <div className="h-full rounded-full bg-brand-500" style={{ width: `${(c.qt_votos / maxVotos) * 100}%` }} />
                    </div>
                    <span className="text-xs sm:text-sm font-bold text-gray-900 w-14 text-right">{Number(c.qt_votos).toLocaleString('pt-BR')}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        )
      })}
      {bairros.length === 0 && <p className="text-sm text-gray-400 text-center py-8">Sem dados por bairro</p>}
    </div>
  )
}

function CandidateBar({ ranking, nm_urna_candidato, nr_candidato, partido, qt_votos, pct_votos, ds_sit_tot_turno, maxVotes, compact, onClick }: any) {
  const isElected = ds_sit_tot_turno?.includes('ELEITO') && !ds_sit_tot_turno?.includes('NÃO ELEITO')
  const width = maxVotes > 0 ? (qt_votos / maxVotes) * 100 : 0

  return (
    <div onClick={onClick} className={`flex items-center gap-2 sm:gap-3 px-2 sm:px-3 rounded-xl hover:bg-gray-50 transition-colors cursor-pointer ${compact ? 'py-2' : 'py-3'}`}>
      <div className={`flex-shrink-0 w-7 h-7 sm:w-8 sm:h-8 rounded-lg flex items-center justify-center text-xs font-bold
        ${ranking <= 1 ? 'bg-amber-50 text-amber-600' : ranking <= 3 ? 'bg-brand-50 text-brand-600' : 'bg-gray-50 text-gray-400'}`}>
        {ranking}º
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center justify-between gap-2">
          <div className="flex items-center gap-1.5 sm:gap-2 min-w-0 flex-wrap">
            <span className={`font-semibold text-gray-900 truncate ${compact ? 'text-xs sm:text-sm' : 'text-sm sm:text-base'}`}>{nm_urna_candidato}</span>
            <span className="text-[10px] font-mono text-gray-400 flex-shrink-0">{nr_candidato}</span>
            {partido && <span className="text-[10px] font-semibold text-gray-500 bg-gray-100 px-1.5 py-0.5 rounded flex-shrink-0 hidden sm:inline">{partido}</span>}
            {isElected && <span className="text-[10px] font-bold text-green-700 bg-green-50 px-1.5 py-0.5 rounded-full flex-shrink-0">ELEITO</span>}
          </div>
          <div className="flex items-center gap-1.5 sm:gap-2 flex-shrink-0">
            <span className={`font-bold text-gray-900 ${compact ? 'text-xs sm:text-sm' : 'text-sm sm:text-base'}`}>{Number(qt_votos).toLocaleString('pt-BR')}</span>
            <span className="text-[10px] sm:text-xs text-gray-400 w-10 sm:w-14 text-right">{Number(pct_votos).toFixed(1)}%</span>
          </div>
        </div>
        <div className={`bg-gray-100 rounded-full overflow-hidden ${compact ? 'h-1 mt-1' : 'h-1.5 sm:h-2 mt-1.5'}`}>
          <div className={`h-full rounded-full transition-all ${isElected ? 'bg-gradient-to-r from-green-400 to-green-600' : 'bg-gradient-to-r from-brand-400 to-brand-600'}`}
            style={{ width: `${width}%` }} />
        </div>
      </div>
    </div>
  )
}
